import { formatDateTimeBR } from "../helpers/format";
import {
  STATUS_MOTORISTA,
  STATUS_OPERACAO_PATIO_SERVICOS,
  STATUS_OPERACOES_PATIO_TRIAGEM,
  STATUS_OPERACOES_PORTO_AGENDADA,
  STATUS_PAGAMENTO,
  STATUS_VEICULO,
} from "../helpers/status";

export type AuditChangeValue = {
  anterior?: unknown;
  atual?: unknown;
};

export type AuditHistoryRecord = Record<string, unknown> & {
  data_historico?: string | null;
  usuario?: string | null;
  alteracoes?: Record<string, AuditChangeValue> | null;
};

export type AuditFieldLabels = Record<string, string>;

export type NormalizedAuditChange = {
  field: string;
  fieldLabel: string;
  previousRawValue?: unknown;
  currentRawValue?: unknown;
  previousValue: string;
  currentValue: string;
  dataHistorico?: string | null;
  usuario?: string | null;
  identifierLabel?: string;
  identifierValue?: string;
};

type StatusOption = {
  id: number;
  label: string;
  color?: string;
  icon?: string;
};

const technicalKeys = new Set([
  "alteracoes",
  "data_historico",
  "usuario",
  "id_usuario_historico",
  "created_at",
  "updated_at",
]);

export const humanizeFieldName = (field: string, labels?: AuditFieldLabels) => {
  if (labels?.[field]) return labels[field];

  return field
    .replace(/^id_/, "")
    .replaceAll("_", " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const formatAuditDate = (value?: string | null) => {
  if (!value) return "---";

  try {
    return formatDateTimeBR(value.includes("T") ? value : value.replace(" ", "T"));
  } catch {
    return value;
  }
};

const normalizeText = (value?: string) => (
  value
    ?.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase() ?? ""
);

export const isStatusAuditField = (field?: string) => field === "status";

export const findStatusOptions = (context?: string): StatusOption[] | undefined => {
  const normalizedContext = normalizeText(context);

  if (normalizedContext.includes("triagen")) return STATUS_OPERACOES_PATIO_TRIAGEM;
  if (normalizedContext.includes("agendada") || normalizedContext.includes("porto")) return STATUS_OPERACOES_PORTO_AGENDADA;
  if (normalizedContext.includes("motorista")) return STATUS_MOTORISTA;
  if (normalizedContext.includes("veiculo")) return STATUS_VEICULO;
  if (normalizedContext.includes("servico")) return STATUS_OPERACAO_PATIO_SERVICOS;
  if (normalizedContext.includes("pagamento")) return STATUS_PAGAMENTO;

  return undefined;
};

export const getAuditStatusOption = (value: unknown, context?: string) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return undefined;

  const statusOptions = findStatusOptions(context);
  return statusOptions?.find((status) => status.id === numericValue);
};

const formatStatusValue = (value: unknown, context?: string) => {
  const statusLabel = getAuditStatusOption(value, context)?.label;
  if (statusLabel) return statusLabel;

  return formatAuditValue(value);
};

export const formatAuditValue = (value: unknown, field?: string, statusContext?: string): string => {
  if (value === null || value === undefined || value === "") return "---";
  if (isStatusAuditField(field)) return formatStatusValue(value, statusContext);
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}(T|\s)\d{2}:\d{2}/.test(value)) {
      return formatAuditDate(value);
    }
    return value;
  }

  if (typeof value === "object") {
    const objectValue = value as Record<string, unknown>;
    const preferredValue =
      objectValue.nome ??
      objectValue.razao_social ??
      objectValue.nome_fantasia ??
      objectValue.label ??
      objectValue.descricao;

    if (preferredValue !== undefined && preferredValue !== null) {
      return String(preferredValue);
    }

    try {
      return JSON.stringify(value);
    } catch {
      return "---";
    }
  }

  return String(value);
};

export const getAuditIdentifier = (record: AuditHistoryRecord, labels?: AuditFieldLabels) => {
  const identifierKey = Object.keys(record).find((key) => {
    const value = record[key];
    return (
      !technicalKeys.has(key) &&
      (key === "id" || key.startsWith("id_")) &&
      (typeof value === "string" || typeof value === "number")
    );
  });

  if (!identifierKey) return {};

  return {
    identifierLabel: humanizeFieldName(identifierKey, labels),
    identifierValue: formatAuditValue(record[identifierKey]),
  };
};

export const getAuditChanges = (
  record?: AuditHistoryRecord | null,
  labels?: AuditFieldLabels,
  statusContext?: string
): NormalizedAuditChange[] => {
  if (!record?.alteracoes || typeof record.alteracoes !== "object") return [];

  const identifier = getAuditIdentifier(record, labels);

  return Object.entries(record.alteracoes).map(([field, change]) => ({
    field,
    fieldLabel: humanizeFieldName(field, labels),
    previousRawValue: change?.anterior,
    currentRawValue: change?.atual,
    previousValue: formatAuditValue(change?.anterior, field, statusContext),
    currentValue: formatAuditValue(change?.atual, field, statusContext),
    dataHistorico: record.data_historico,
    usuario: record.usuario,
    ...identifier,
  }));
};

export const hasAuditChanges = (record?: AuditHistoryRecord | null) => (
  getAuditChanges(record).length > 0
);

export const summarizeAuditChanges = (
  record?: AuditHistoryRecord | null,
  labels?: AuditFieldLabels,
  statusContext?: string
) => {
  const changes = getAuditChanges(record, labels, statusContext);
  if (changes.length === 0) return "Nenhuma alteração";

  return changes
    .map((change) => `${change.fieldLabel}: ${change.previousValue} -> ${change.currentValue}`)
    .join(" | ");
};
